import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { FormField } from '~/components/system/form-field';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { useWorkCalendarMutations } from '~/hooks/use-available-dates';
import { workCalendarSchema, EMPTY_WORK_CALENDAR, type WorkCalendarFormValues } from '~/lib/schemas/available-date';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	initial: WorkCalendarFormValues | null;
}

/** Add / edit a work calendar (year + weekly-limit + visible + sort). */
export function WorkCalendarEditDialog({ open, onOpenChange, initial }: Props) {
	const { t } = useTranslation();
	const { create, update } = useWorkCalendarMutations();
	const form = useForm<WorkCalendarFormValues>({
		resolver: zodResolver(workCalendarSchema) as Resolver<WorkCalendarFormValues>,
		defaultValues: initial ?? EMPTY_WORK_CALENDAR,
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, setValue, watch, formState } = form;
	const isEdit = Boolean(initial?.id);

	useEffect(() => {
		if (open) reset(initial ?? EMPTY_WORK_CALENDAR);
	}, [open, initial, reset]);

	const onSubmit = handleSubmit(async values => {
		try {
			if (values.id) await update.mutateAsync(values);
			else await create.mutateAsync(values);
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	const wlmt = watch('wlmt');
	const visible = watch('iv');

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{isEdit ? t('system.availableDates.editTitle') : t('system.availableDates.addTitle')}</DialogTitle>
					<DialogDescription>{t('system.availableDates.formSubtitle')}</DialogDescription>
				</DialogHeader>
				<form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
					<div className="grid grid-cols-2 gap-4">
						<FormField label={t('system.availableDates.columns.year')} error={formState.errors.year?.message}>
							<Input type="number" {...register('year', { valueAsNumber: true })} aria-invalid={Boolean(formState.errors.year)} autoFocus />
						</FormField>
						<FormField label={t('system.availableDates.columns.sort')} error={formState.errors.si?.message}>
							<Input type="number" {...register('si', { valueAsNumber: true })} aria-invalid={Boolean(formState.errors.si)} />
						</FormField>
					</div>
					<div className="flex items-center justify-between rounded-md border border-border px-4 py-3">
						<Label htmlFor="wc-wlmt">{t('system.availableDates.columns.weeklyLimit')}</Label>
						<Switch id="wc-wlmt" checked={wlmt} onCheckedChange={next => setValue('wlmt', next, { shouldDirty: true })} />
					</div>
					<div className="flex items-center justify-between rounded-md border border-border px-4 py-3">
						<Label htmlFor="wc-visible">{t('system.availableDates.columns.visible')}</Label>
						<Switch id="wc-visible" checked={visible} onCheckedChange={next => setValue('iv', next, { shouldDirty: true })} />
					</div>
					<DialogFooter>
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" disabled={create.isPending || update.isPending}>
							{create.isPending || update.isPending ? t('common.saving') : t('common.save')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
